import Settings from "Data/Settings";
import { Elastic, Sine, TimelineLite, TimelineMax, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import {DWELL_DURATION} from "Data/Constants";

export class Button extends HLF.TextureSwapButton {
    protected _hoverVO: string;
    protected _clickedSFX: string | undefined;

    protected dwellTimeline?: TimelineLite;
    protected isCursorDwelling: boolean = false;

    protected _subscriptions: any[] = [];

    protected _hintTimeline?: TimelineMax;
    protected isOver: boolean = false;

    constructor (
        pCallback: () => void,
        pAsset: string,
        pOverAsset: string,
        pDownAsset: string,
        pHoverVO: string,
        pClickedSFX: string,
    ) {
        super(pCallback, pAsset, pOverAsset, pDownAsset, undefined as any as string);

        this._hoverVO = pHoverVO;
        this._clickedSFX = pClickedSFX;

        // for accessibility
        this.addListener(HLF.Input.Events.CLICK, (e)=>{
            this.onPointerDown(e);
            this.onPointerUp(e);
        });

        this.scale.set(Settings.buttonSizeMultiplier);

        this._subscriptions.push(
            PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this)),
        );
    }

    public destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]): void {
        super.destroy(pOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
        this._hintTimeline?.kill();
    }

    public pulse(scale: number = 1.15, interval: number = 1.5, startImmediately: boolean = true) {
        this._hintTimeline?.kill();
        this._hintTimeline = new TimelineMax({repeat: -1});
        this._hintTimeline
            .to(this, 0.15, {
                pixi: {
                    scale: Settings.buttonSizeMultiplier * scale
                },
                ease: Sine.easeInOut,
            }, interval)
            .to(this, 1.0, {
                pixi: {
                    scale: Settings.buttonSizeMultiplier,
                },
                ease: Elastic.easeOut,
            })
            .call(()=>{
                this._hintTimeline?.seek(0);
                if (this.isOver) {
                    this._hintTimeline?.pause();
                }
            })

        if (startImmediately) {
            this._hintTimeline.seek(interval);
        }
    }

    public stopPulse() {
        this._hintTimeline?.kill();
        this._hintTimeline?.clear();
        this._hintTimeline?.to(this, 0.15, {pixi: {scale: Settings.buttonSizeMultiplier}, ease: Elastic.easeOut});
    }

    public onFocusBegin(): void {
        super.onFocusBegin();
        this.playKeyboardFocusVO();
        this._image!.texture = this._overTexture;
    }

    public onFocusEnd(): void {
        super.onFocusEnd();
        this._image!.texture = this._normalTexture;
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.playClickedSFX();
        }
        super.onFocusActivated();
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOver(pEvent);
        this.playHoverVO();
        if (Settings.useDwellActivation) {
            this.beginDwell();
        }
        this.isOver = true;
    }

    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        super.onPointerDown(pEvent);
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        if (this._eventData !== undefined) {
            this.playClickedSFX();
        }
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        super.onPointerOut(pEvent);
        this.isOver = false;
        this._hintTimeline?.resume();
    }

    protected playKeyboardFocusVO(): void {
        HLF.Application.instance.voiceover.stopVO();
        HLF.Application.instance.voiceover.playVO(this._hoverVO, { priority: -1, mode: HLF.PlayMode.Override });
    }

    protected playHoverVO(): void {
        HLF.Application.instance.voiceover.playVO(this._hoverVO, { priority: -1, mode: HLF.PlayMode.Override });
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSFX, 1, false, HLF.AudioCategory.SFX));
        }
    }

    protected beginDwell() {
        this.isCursorDwelling = true;
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        this.dwellTimeline = new TimelineLite();
        this.dwellTimeline.call(()=>PubSub.publishSync(Topics.DWELL_BEGIN, this), undefined, undefined, 0);
        this.dwellTimeline.call(()=>this.completeDwell(), undefined, undefined, DWELL_DURATION);
    }

    protected cancelDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        if (this.isCursorDwelling) {
            this.isCursorDwelling = false;
            PubSub.publishSync(Topics.DWELL_CANCELLED, this);
        }
    }

    protected completeDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        PubSub.publishSync(Topics.DWELL_ACTIVATED, this);
        this.isCursorDwelling = false;
        this.onFocusActivated(); // TODO: swap texture to down texture?
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        this.scale.set(pSettings.buttonSizeMultiplier);
    }
}
